import { AxiosError, AxiosResponse } from "axios";
import { CookieManager } from "./CookieManager";
import { ContentService } from "@Api/ContentService";
import CouponModalTemplate from "@Base/Templates/CouponModal.hbs";
import { CouponService } from "@Api/CouponService";
import { HeaderBase } from "@Base/Site/HeaderBase";
import Modal from "bootstrap/js/dist/modal.js";
import { EngagementProvider } from "@Base/Engagement/Engagement";
import { Tenant } from "./Tenant";
import { ModalTools } from "./ModalTools";
import { TextResource } from "./TextResource";
import queryString from "query-string";
import { TrackJS } from "trackjs";

export class CouponManager
{
	private CouponCookie = CookieManager.Cookies.Coupon;
	private couponService: CouponService;
	private engagementProvider: EngagementProvider;

	constructor()
	{
		this.couponService = new CouponService();
		this.engagementProvider = new EngagementProvider();
	}

	public get CouponID(): string
	{
		return ContentService.Response.CouponID.toString();
	}

	public get CouponMismatch(): boolean
	{
		return (this.CouponID !== this.PreviousCouponID);
	}

	public get CouponValid(): boolean
	{
		return ContentService.Response.CouponIsValid;
	}

	public get PreviousCouponID(): string
	{
		return CookieManager.GetCookie(this.CouponCookie.Key, this.CouponCookie.PreviousCouponID);
	}

	public ApplyCoupon = (couponCode): void =>
	{
		this.couponService.ApplyCoupon(couponCode)
			.then((response: AxiosResponse) =>
			{
				if (response.data !== "undefined")
				{
					const banner = response.data.replace("<br>", "<br />");
					HeaderBase.AddToBannerSection(banner);
				}
			})
			.catch((error: AxiosError) =>
			{
				console.error(error);
			});
	};

	public RemoveCoupon = (): void =>
	{
		this.couponService.RemoveCoupon();
	};

	public DisplayCouponDetails = (banner: HTMLElement): void =>
	{
		const details = banner.getAttribute("data-details");
		const title = banner.parentElement.textContent.replace(TextResource.GetTextByCulture("Feature_Coupon_Details"), "");

		ModalTools.DisplayAlert(details, title);
	};

	/**
	 * Display the active coupon in a modal
	 */
	private DisplayCouponModal = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			const couponResponse = ContentService.Response.CouponBanner;
			const parser = new DOMParser();
			const couponHtml = parser.parseFromString(couponResponse, "text/html");
			let couponText = "";
			const logo: HTMLImageElement = document.querySelector(".navbar-brand img");
			let logoClass = "";
			const logoPath = logo.src;
			const logoWidth = logo.width;
			const logoHeight = logo.height;
			let modalClass = "";
			let modalContentClass = "";
			let monsterPath = "";
			let monsterWidth = "";
			let monsterHeight = "";
			let statusHeadline = "";
			let statusSubtitle = "";
			const validCoupon = this.CouponValid;

			if (this.CouponMismatch || !validCoupon)
			{
				if (couponResponse !== "" && couponResponse !== null)
				{
					const couponBanner = couponHtml.querySelector(".banner-text");

					if (couponBanner !== null)
					{
						couponText = couponBanner.innerHTML;
					}
				}

				if (validCoupon)
				{
					modalClass = "coupon-modal--valid";
					statusHeadline = TextResource.GetTextByCulture("Feature_Coupon_Success_Headline");
					statusSubtitle = TextResource.GetTextByCulture("Feature_Coupon_Success_Subtitle");
				}
				else
				{
					modalClass = "coupon-modal--invalid";
					statusHeadline = TextResource.GetTextByCulture("Feature_Coupon_Invalid_Headline");
					statusSubtitle = TextResource.GetTextByCulture("Feature_Coupon_Invalid_Subtitle");

					console.log("Coupon Invalid");

					if (window.trackJs)
					{
						TrackJS.addMetadata("couponCookie", CookieManager.GetCookie(this.CouponCookie.Key));
						TrackJS.addMetadata("countryID", CookieManager.GetCookie(CookieManager.Cookies.Visitor.Key, CookieManager.Cookies.Visitor.CountryID));
						TrackJS.addMetadata("personalizationID", CookieManager.GetCookie(CookieManager.Cookies.Unique.Key));
						TrackJS.addMetadata("ogid", CookieManager.GetCookie("CART_v1"));
					}
				}

				switch (Tenant.DomainProjectID)
				{
					case 13:
					case 140:
					case 164:
					case 165:
					case 171:
					case 173:
						logoClass = "filter-hc-red";
						monsterWidth = "251";
						monsterHeight = "134";
						monsterPath = "/wwwroot/img/monsters/monster-peek-puppet.png";

						break;

					case 159:
					case 167:
					case 170:
						modalContentClass = "bg-primary text-white";
						logoClass = "filter-white";
						monsterWidth = "235";
						monsterHeight = "144";
						monsterPath = "/wwwroot/img/monsters/monster-peek.png";

						break;

					case 184:
						modalContentClass = "bg-dark text-white";
						logoClass = "";
						monsterWidth = "251";
						monsterHeight = "134";
						monsterPath = "/wwwroot/img/monsters/monster-peek-puppet.png";

						break;

					case 187:
					case 188:
						modalContentClass = "bg-primary text-white";
						logoClass = "";
						monsterWidth = "251";
						monsterHeight = "134";
						monsterPath = "/wwwroot/img/monsters/monster-peek-puppet.png";

						break;
				}

				const couponModalHtml = CouponModalTemplate({
					closeLabel: TextResource.GetTextByCulture("Word_Close"),
					couponText: couponText,
					domainProjectID: Tenant.DomainProjectID,
					logoAltText: TextResource.FormatText("Feature_Coupon_Logo_AltText", [Tenant.WebsiteName]),
					logoClass: logoClass,
					logoPath: logoPath,
					logoWidth: logoWidth,
					logoHeight: logoHeight,
					modalClass: modalClass,
					modalContentClass: modalContentClass,
					monsterAltText: TextResource.FormatText("Feature_Coupon_Monster_AltText", [Tenant.WebsiteName]),
					monsterPath: monsterPath,
					monsterWidth: monsterWidth,
					monsterHeight: monsterHeight,
					statusHeadline: statusHeadline,
					statusSubtitle: statusSubtitle,
					validCoupon: validCoupon
				});

				const couponModalNode = parser.parseFromString(couponModalHtml, "text/html");
				document.body.appendChild(couponModalNode.body.firstChild);
				const couponModalSelector = document.querySelector("#couponModal");
				const couponModalInstance = Modal.getOrCreateInstance(couponModalSelector);

				couponModalInstance.show();

				couponModalSelector.addEventListener("hidden.bs.modal", (event) =>
				{
					ModalTools.ModalDisposer(event.currentTarget);
				});

				if (!validCoupon)
				{
					couponModalSelector.addEventListener("hidden.bs.modal", () =>
					{
						this.couponService.RemoveCoupon().then(() =>
						{
							window.setTimeout(() =>
							{
								this.engagementProvider.DownloadSdk();
							}, 1000);
						});
					});
				}
			}
		}
	};

	private ProcessCouponDisplay = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			const banner = ContentService.Response.CouponBanner;

			if (banner !== undefined && banner !== null && banner !== "")
			{
				const headerMessages = document.querySelector("header .msgs");
				const couponBannerSelector = document.querySelector("#cpn");

				//insert a banner style coupon if the expand banner container is selected in admin. used (pretty much) exclusively for sitewide coupons like the post season sale
				if (headerMessages.classList.contains("msgs--open") && couponBannerSelector === null)
				{
					const couponBanner = `<div id="cpn">${banner.replace("<br>", "<br />")}</div>`;
					HeaderBase.AddToBannerSection(couponBanner);
				}
				else
				{
					this.DisplayCouponModal();
				}

				if (this.CouponMismatch)
				{
					CookieManager.SetInCookie(this.CouponCookie.Key, this.CouponCookie.PreviousCouponID, this.CouponID);
				}
			}
		}
	};

	public TriggerContentCallbacks = (): void =>
	{
		this.ProcessCouponDisplay();
	};
}